import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, User, Heart, GraduationCap, Briefcase, Languages, Award, Shield, AlertTriangle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

function Input({ label, value, onChange, type = "text", placeholder = "", required = false, testId }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">{label}</label>
      <input data-testid={testId} type={type} value={value} onChange={onChange} placeholder={placeholder} required={required}
        className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" />
    </div>
  );
}

export default function EmployeeProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [dependents, setDependents] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [contactDialog, setContactDialog] = useState(false);
  const [depDialog, setDepDialog] = useState(false);
  const [qualDialog, setQualDialog] = useState(false);

  // Forms
  const [contactForm, setContactForm] = useState({ name: "", relationship: "", phone: "", email: "", address: "" });
  const [depForm, setDepForm] = useState({ name: "", relationship: "", date_of_birth: "", gender: "" });
  const [qualForm, setQualForm] = useState({ type: "education", title: "", institution: "", field_of_study: "", level: "", start_date: "", end_date: "", proficiency: "", issuing_body: "", expiry_date: "", description: "" });

  // Termination
  const [terminateDialog, setTerminateDialog] = useState(false);
  const [terminationReasons, setTerminationReasons] = useState([]);
  const [terminateForm, setTerminateForm] = useState({ date_of_termination: "", reason: "", notes: "" });
  const [terminating, setTerminating] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [emp, ec, dep, qual] = await Promise.all([
        API.get(`/employees/${id}`),
        API.get(`/employees/${id}/emergency-contacts`),
        API.get(`/employees/${id}/dependents`),
        API.get(`/employees/${id}/qualifications`),
      ]);
      setEmployee(emp.data);
      setContacts(ec.data);
      setDependents(dep.data);
      setQualifications(qual.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Load termination reasons
  useEffect(() => {
    API.get("/admin/termination-reasons")
      .then(({ data }) => setTerminationReasons(data.reasons || []))
      .catch(() => {});
  }, []);

  const handleTerminate = async (e) => {
    e.preventDefault();
    if (!terminateForm.date_of_termination || !terminateForm.reason) {
      toast.error("Date of termination and reason are required");
      return;
    }
    setTerminating(true);
    try {
      await API.post(`/employees/${id}/terminate`, terminateForm);
      toast.success("Employee terminated successfully");
      setTerminateDialog(false);
      setTerminateForm({ date_of_termination: "", reason: "", notes: "" });
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTerminating(false);
    }
  };

  const addContact = async (e) => {
    e.preventDefault();
    try { await API.post(`/employees/${id}/emergency-contacts`, contactForm); toast.success("Contact added"); setContactDialog(false); setContactForm({ name: "", relationship: "", phone: "", email: "", address: "" }); fetchAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteContact = async (cid) => {
    await API.delete(`/employees/${id}/emergency-contacts/${cid}`); toast.success("Deleted"); fetchAll();
  };

  const addDependent = async (e) => {
    e.preventDefault();
    try { await API.post(`/employees/${id}/dependents`, depForm); toast.success("Dependent added"); setDepDialog(false); setDepForm({ name: "", relationship: "", date_of_birth: "", gender: "" }); fetchAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteDependent = async (did) => {
    await API.delete(`/employees/${id}/dependents/${did}`); toast.success("Deleted"); fetchAll();
  };

  const addQualification = async (e) => {
    e.preventDefault();
    try { await API.post(`/employees/${id}/qualifications`, qualForm); toast.success("Qualification added"); setQualDialog(false); setQualForm({ type: "education", title: "", institution: "", field_of_study: "", level: "", start_date: "", end_date: "", proficiency: "", issuing_body: "", expiry_date: "", description: "" }); fetchAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteQual = async (qid) => {
    await API.delete(`/employees/${id}/qualifications/${qid}`); toast.success("Deleted"); fetchAll();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-sm text-slate-500">Loading profile...</div></div>;
  if (!employee) return <div className="text-center py-12 text-sm text-slate-500">Employee not found</div>;

  const education = qualifications.filter((q) => q.type === "education");
  const experience = qualifications.filter((q) => q.type === "experience");
  const skills = qualifications.filter((q) => q.type === "skill");
  const certs = qualifications.filter((q) => q.type === "certification");
  const languages = qualifications.filter((q) => q.type === "language");

  return (
    <div className="space-y-6" data-testid="employee-profile-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/employees")} className="p-2 hover:bg-slate-100 rounded-sm transition-colors" data-testid="back-to-employees">
          <ArrowLeft className="h-4 w-4 text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>{employee.name}</h1>
          <p className="text-sm text-slate-500">{employee.job_title} — {employee.department}</p>
        </div>
        <Badge variant="outline" className="ml-auto capitalize text-xs">{(employee.role || "employee").replace("_", " ")}</Badge>
        {employee.status === "active" && (
          <button
            onClick={() => setTerminateDialog(true)}
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1"
            data-testid="terminate-employee-btn"
          >
            <AlertTriangle className="h-3 w-3" /> Terminate
          </button>
        )}
      </div>

      {/* Employee Info Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-white border border-slate-200 rounded-sm p-5">
        {[["Email", employee.email], ["Phone", employee.phone || "—"], ["ID Number", employee.id_number || "—"], ["Salary", `KES ${Number(employee.basic_salary || 0).toLocaleString()}`]].map(([l, v]) => (
          <div key={l}><span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1">{l}</span><span className="text-sm text-slate-900">{v}</span></div>
        ))}
        {employee.start_date && (
          <div><span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1">Date of Employment</span><span className="text-sm text-slate-900">{employee.start_date}</span></div>
        )}
        {employee.status === "inactive" && (
          <>
            {employee.date_of_termination && (
              <div><span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1">Date of Termination</span><span className="text-sm text-red-600">{employee.date_of_termination}</span></div>
            )}
            {employee.termination_reason && (
              <div><span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1">Termination Reason</span><span className="text-sm text-red-600">{employee.termination_reason}</span></div>
            )}
          </>
        )}
      </div>

      <Tabs defaultValue="emergency" className="w-full">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="emergency" data-testid="tab-emergency" className="flex items-center gap-1.5 text-xs sm:text-sm"><Shield className="h-3.5 w-3.5" />Next of Kin</TabsTrigger>
          <TabsTrigger value="dependents" data-testid="tab-dependents" className="flex items-center gap-1.5 text-xs sm:text-sm"><Heart className="h-3.5 w-3.5" />Dependents</TabsTrigger>
          <TabsTrigger value="education" data-testid="tab-education" className="flex items-center gap-1.5 text-xs sm:text-sm"><GraduationCap className="h-3.5 w-3.5" />Education</TabsTrigger>
          <TabsTrigger value="experience" data-testid="tab-experience" className="flex items-center gap-1.5 text-xs sm:text-sm"><Briefcase className="h-3.5 w-3.5" />Experience</TabsTrigger>
          <TabsTrigger value="skills" data-testid="tab-skills" className="flex items-center gap-1.5 text-xs sm:text-sm"><Award className="h-3.5 w-3.5" />Skills & Certs</TabsTrigger>
          <TabsTrigger value="languages" data-testid="tab-languages" className="flex items-center gap-1.5 text-xs sm:text-sm"><Languages className="h-3.5 w-3.5" />Languages</TabsTrigger>
        </TabsList>

        {/* Emergency Contacts */}
        <TabsContent value="emergency">
          <SectionHeader title="Emergency Contacts / Next of Kin" onAdd={() => setContactDialog(true)} testId="add-contact-btn" />
          <DataTable headers={["Name", "Relationship", "Phone", "Email", "Address", ""]} empty={contacts.length === 0} emptyText="No emergency contacts added">
            {contacts.map((c) => (
              <TableRow key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`contact-row-${c.id}`}>
                <TableCell className="px-4 py-3 text-sm font-medium text-slate-900">{c.name}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700">{c.relationship}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700">{c.phone}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700">{c.email}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700 hidden md:table-cell">{c.address}</TableCell>
                <TableCell className="px-4 py-3 text-right"><DelBtn onClick={() => deleteContact(c.id)} testId={`del-contact-${c.id}`} /></TableCell>
              </TableRow>
            ))}
          </DataTable>
        </TabsContent>

        {/* Dependents */}
        <TabsContent value="dependents">
          <SectionHeader title="Dependents" onAdd={() => setDepDialog(true)} testId="add-dependent-btn" />
          <DataTable headers={["Name", "Relationship", "Date of Birth", "Gender", ""]} empty={dependents.length === 0} emptyText="No dependents added">
            {dependents.map((d) => (
              <TableRow key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`dep-row-${d.id}`}>
                <TableCell className="px-4 py-3 text-sm font-medium text-slate-900">{d.name}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700">{d.relationship}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700">{d.date_of_birth}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700 capitalize">{d.gender}</TableCell>
                <TableCell className="px-4 py-3 text-right"><DelBtn onClick={() => deleteDependent(d.id)} testId={`del-dep-${d.id}`} /></TableCell>
              </TableRow>
            ))}
          </DataTable>
        </TabsContent>

        {/* Education */}
        <TabsContent value="education">
          <SectionHeader title="Education" onAdd={() => { setQualForm({ ...qualForm, type: "education" }); setQualDialog(true); }} testId="add-education-btn" />
          <DataTable headers={["Qualification", "Institution", "Field of Study", "Level", "Year", ""]} empty={education.length === 0} emptyText="No education records">
            {education.map((q) => (
              <TableRow key={q.id} className="border-b border-slate-100 hover:bg-slate-50">
                <TableCell className="px-4 py-3 text-sm font-medium text-slate-900">{q.title}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700">{q.institution}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700">{q.field_of_study}</TableCell>
                <TableCell className="px-4 py-3"><Badge variant="outline" className="text-xs capitalize">{q.level || "—"}</Badge></TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700">{q.end_date || q.start_date}</TableCell>
                <TableCell className="px-4 py-3 text-right"><DelBtn onClick={() => deleteQual(q.id)} /></TableCell>
              </TableRow>
            ))}
          </DataTable>
        </TabsContent>

        {/* Work Experience */}
        <TabsContent value="experience">
          <SectionHeader title="Work Experience" onAdd={() => { setQualForm({ ...qualForm, type: "experience" }); setQualDialog(true); }} testId="add-experience-btn" />
          <DataTable headers={["Position", "Company", "From", "To", "Description", ""]} empty={experience.length === 0} emptyText="No work experience records">
            {experience.map((q) => (
              <TableRow key={q.id} className="border-b border-slate-100 hover:bg-slate-50">
                <TableCell className="px-4 py-3 text-sm font-medium text-slate-900">{q.title}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700">{q.institution}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700">{q.start_date}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700">{q.end_date || "Present"}</TableCell>
                <TableCell className="px-4 py-3 text-sm text-slate-700 max-w-[200px] truncate">{q.description}</TableCell>
                <TableCell className="px-4 py-3 text-right"><DelBtn onClick={() => deleteQual(q.id)} /></TableCell>
              </TableRow>
            ))}
          </DataTable>
        </TabsContent>

        {/* Skills & Certifications */}
        <TabsContent value="skills">
          <SectionHeader title="Skills & Certifications" onAdd={() => { setQualForm({ ...qualForm, type: "skill" }); setQualDialog(true); }} testId="add-skill-btn" />
          <div className="space-y-6">
            {skills.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.map((s) => (
                    <div key={s.id} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-sm px-3 py-1.5 text-sm">
                      <span className="text-slate-900">{s.title}</span>
                      {s.proficiency && <Badge variant="outline" className="text-xs">{s.proficiency}</Badge>}
                      <button onClick={() => deleteQual(s.id)} className="ml-1 text-slate-400 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <SectionHeader title="Certifications & Licenses" onAdd={() => { setQualForm({ ...qualForm, type: "certification" }); setQualDialog(true); }} testId="add-cert-btn" small />
            <DataTable headers={["Certificate", "Issuing Body", "Issue Date", "Expiry", ""]} empty={certs.length === 0} emptyText="No certifications">
              {certs.map((c) => (
                <TableRow key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <TableCell className="px-4 py-3 text-sm font-medium text-slate-900">{c.title}</TableCell>
                  <TableCell className="px-4 py-3 text-sm text-slate-700">{c.issuing_body}</TableCell>
                  <TableCell className="px-4 py-3 text-sm text-slate-700">{c.start_date}</TableCell>
                  <TableCell className="px-4 py-3 text-sm text-slate-700">{c.expiry_date || "—"}</TableCell>
                  <TableCell className="px-4 py-3 text-right"><DelBtn onClick={() => deleteQual(c.id)} /></TableCell>
                </TableRow>
              ))}
            </DataTable>
          </div>
        </TabsContent>

        {/* Languages */}
        <TabsContent value="languages">
          <SectionHeader title="Languages" onAdd={() => { setQualForm({ ...qualForm, type: "language" }); setQualDialog(true); }} testId="add-language-btn" />
          <DataTable headers={["Language", "Proficiency", ""]} empty={languages.length === 0} emptyText="No languages added">
            {languages.map((l) => (
              <TableRow key={l.id} className="border-b border-slate-100 hover:bg-slate-50">
                <TableCell className="px-4 py-3 text-sm font-medium text-slate-900">{l.title}</TableCell>
                <TableCell className="px-4 py-3"><Badge variant="outline" className="text-xs capitalize">{l.proficiency || "—"}</Badge></TableCell>
                <TableCell className="px-4 py-3 text-right"><DelBtn onClick={() => deleteQual(l.id)} /></TableCell>
              </TableRow>
            ))}
          </DataTable>
        </TabsContent>
      </Tabs>

      {/* Emergency Contact Dialog */}
      <Dialog open={contactDialog} onOpenChange={setContactDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Emergency Contact</DialogTitle><DialogDescription>Next of kin / emergency contact details</DialogDescription></DialogHeader>
          <form onSubmit={addContact} className="space-y-3 py-2" data-testid="contact-form">
            <Input label="Full Name" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} required testId="contact-name" />
            <Input label="Relationship" value={contactForm.relationship} onChange={(e) => setContactForm({ ...contactForm, relationship: e.target.value })} placeholder="e.g. Spouse, Parent, Sibling" required testId="contact-relationship" />
            <Input label="Phone" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="+254..." required testId="contact-phone" />
            <Input label="Email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} type="email" testId="contact-email" />
            <Input label="Address" value={contactForm.address} onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })} testId="contact-address" />
            <DialogFooter className="pt-2">
              <button type="button" onClick={() => setContactDialog(false)} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-4 py-2 text-sm">Cancel</button>
              <button type="submit" data-testid="contact-submit" className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-4 py-2 text-sm">Add Contact</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dependent Dialog */}
      <Dialog open={depDialog} onOpenChange={setDepDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Dependent</DialogTitle><DialogDescription>Family member or dependent details</DialogDescription></DialogHeader>
          <form onSubmit={addDependent} className="space-y-3 py-2" data-testid="dependent-form">
            <Input label="Full Name" value={depForm.name} onChange={(e) => setDepForm({ ...depForm, name: e.target.value })} required testId="dep-name" />
            <Input label="Relationship" value={depForm.relationship} onChange={(e) => setDepForm({ ...depForm, relationship: e.target.value })} placeholder="e.g. Child, Spouse" required testId="dep-relationship" />
            <Input label="Date of Birth" value={depForm.date_of_birth} onChange={(e) => setDepForm({ ...depForm, date_of_birth: e.target.value })} type="date" testId="dep-dob" />
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Gender</label>
              <Select value={depForm.gender} onValueChange={(v) => setDepForm({ ...depForm, gender: v })}>
                <SelectTrigger data-testid="dep-gender" className="h-9"><SelectValue placeholder="Select gender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="pt-2">
              <button type="button" onClick={() => setDepDialog(false)} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-4 py-2 text-sm">Cancel</button>
              <button type="submit" data-testid="dep-submit" className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-4 py-2 text-sm">Add Dependent</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Termination Dialog ── */}
      <Dialog open={terminateDialog} onOpenChange={setTerminateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Terminate Employee
            </DialogTitle>
            <DialogDescription>
              This will set the employee's status to inactive and record the termination details.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTerminate} className="space-y-4 py-2" data-testid="terminate-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                Date of Termination <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={terminateForm.date_of_termination}
                onChange={(e) => setTerminateForm({ ...terminateForm, date_of_termination: e.target.value })}
                className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-500 transition-all text-sm"
                required
                data-testid="terminate-date"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                Reason <span className="text-red-500">*</span>
              </label>
              <Select
                value={terminateForm.reason}
                onValueChange={(v) => setTerminateForm({ ...terminateForm, reason: v })}
              >
                <SelectTrigger data-testid="terminate-reason" className="h-9">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {terminationReasons.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                Notes <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <textarea
                value={terminateForm.notes}
                onChange={(e) => setTerminateForm({ ...terminateForm, notes: e.target.value })}
                rows={3}
                className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-500 transition-all text-sm resize-none"
                placeholder="Additional notes about the termination…"
                data-testid="terminate-notes"
              />
            </div>
            <div className="bg-red-50 border border-red-200 rounded-sm px-3 py-2 text-xs text-red-700">
              <strong>Warning:</strong> This action will mark the employee as inactive. They will no longer be able to log in or access the system.
            </div>
            <DialogFooter className="pt-2">
              <button
                type="button"
                onClick={() => setTerminateDialog(false)}
                className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={terminating || !terminateForm.date_of_termination || !terminateForm.reason}
                className="bg-red-600 hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-sm px-4 py-2 text-sm flex items-center gap-1"
                data-testid="terminate-submit"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                {terminating ? "Terminating…" : "Confirm Termination"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Qualification Dialog */}
      <Dialog open={qualDialog} onOpenChange={setQualDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add {qualForm.type === "education" ? "Education" : qualForm.type === "experience" ? "Work Experience" : qualForm.type === "skill" ? "Skill" : qualForm.type === "certification" ? "Certification" : "Language"}</DialogTitle>
            <DialogDescription>Enter qualification details</DialogDescription></DialogHeader>
          <form onSubmit={addQualification} className="space-y-3 py-2" data-testid="qual-form">
            {(qualForm.type === "education") && (
              <>
                <Input label="Qualification Title" value={qualForm.title} onChange={(e) => setQualForm({ ...qualForm, title: e.target.value })} placeholder="e.g. Bachelor of Science" required testId="qual-title" />
                <Input label="Institution" value={qualForm.institution} onChange={(e) => setQualForm({ ...qualForm, institution: e.target.value })} placeholder="e.g. University of Nairobi" required testId="qual-institution" />
                <Input label="Field of Study" value={qualForm.field_of_study} onChange={(e) => setQualForm({ ...qualForm, field_of_study: e.target.value })} placeholder="e.g. Computer Science" testId="qual-field" />
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Level</label>
                  <Select value={qualForm.level} onValueChange={(v) => setQualForm({ ...qualForm, level: v })}>
                    <SelectTrigger data-testid="qual-level" className="h-9"><SelectValue placeholder="Select level" /></SelectTrigger>
                    <SelectContent>
                      {["Certificate", "Diploma", "Degree", "Postgraduate", "Masters", "PhD"].map((l) => <SelectItem key={l} value={l.toLowerCase()}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Start Year" value={qualForm.start_date} onChange={(e) => setQualForm({ ...qualForm, start_date: e.target.value })} placeholder="2020" testId="qual-start" />
                  <Input label="End Year" value={qualForm.end_date} onChange={(e) => setQualForm({ ...qualForm, end_date: e.target.value })} placeholder="2024" testId="qual-end" />
                </div>
              </>
            )}
            {(qualForm.type === "experience") && (
              <>
                <Input label="Job Title" value={qualForm.title} onChange={(e) => setQualForm({ ...qualForm, title: e.target.value })} required placeholder="e.g. Senior Developer" testId="qual-title" />
                <Input label="Company" value={qualForm.institution} onChange={(e) => setQualForm({ ...qualForm, institution: e.target.value })} required placeholder="e.g. Safaricom" testId="qual-institution" />
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Start Date" value={qualForm.start_date} onChange={(e) => setQualForm({ ...qualForm, start_date: e.target.value })} type="date" testId="qual-start" />
                  <Input label="End Date" value={qualForm.end_date} onChange={(e) => setQualForm({ ...qualForm, end_date: e.target.value })} type="date" testId="qual-end" />
                </div>
                <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Description</label>
                  <textarea data-testid="qual-desc" value={qualForm.description} onChange={(e) => setQualForm({ ...qualForm, description: e.target.value })} rows={2} className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 text-sm resize-none" />
                </div>
              </>
            )}
            {(qualForm.type === "skill") && (
              <>
                <Input label="Skill Name" value={qualForm.title} onChange={(e) => setQualForm({ ...qualForm, title: e.target.value })} required placeholder="e.g. Python, Project Management" testId="qual-title" />
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Proficiency</label>
                  <Select value={qualForm.proficiency} onValueChange={(v) => setQualForm({ ...qualForm, proficiency: v })}>
                    <SelectTrigger data-testid="qual-proficiency" className="h-9"><SelectValue placeholder="Level" /></SelectTrigger>
                    <SelectContent>
                      {["Beginner", "Intermediate", "Advanced", "Expert"].map((l) => <SelectItem key={l} value={l.toLowerCase()}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {(qualForm.type === "certification") && (
              <>
                <Input label="Certificate Name" value={qualForm.title} onChange={(e) => setQualForm({ ...qualForm, title: e.target.value })} required placeholder="e.g. CPA, PMP" testId="qual-title" />
                <Input label="Issuing Body" value={qualForm.issuing_body} onChange={(e) => setQualForm({ ...qualForm, issuing_body: e.target.value })} placeholder="e.g. KASNEB" testId="qual-issuing" />
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Issue Date" value={qualForm.start_date} onChange={(e) => setQualForm({ ...qualForm, start_date: e.target.value })} type="date" testId="qual-start" />
                  <Input label="Expiry Date" value={qualForm.expiry_date} onChange={(e) => setQualForm({ ...qualForm, expiry_date: e.target.value })} type="date" testId="qual-expiry" />
                </div>
              </>
            )}
            {(qualForm.type === "language") && (
              <>
                <Input label="Language" value={qualForm.title} onChange={(e) => setQualForm({ ...qualForm, title: e.target.value })} required placeholder="e.g. Swahili, English" testId="qual-title" />
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Fluency</label>
                  <Select value={qualForm.proficiency} onValueChange={(v) => setQualForm({ ...qualForm, proficiency: v })}>
                    <SelectTrigger data-testid="qual-fluency" className="h-9"><SelectValue placeholder="Select fluency" /></SelectTrigger>
                    <SelectContent>
                      {["Basic", "Conversational", "Fluent", "Native"].map((l) => <SelectItem key={l} value={l.toLowerCase()}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <DialogFooter className="pt-2">
              <button type="button" onClick={() => setQualDialog(false)} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-4 py-2 text-sm">Cancel</button>
              <button type="submit" data-testid="qual-submit" className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-4 py-2 text-sm">Save</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionHeader({ title, onAdd, testId, small }) {
  return (
    <div className={`flex items-center justify-between ${small ? "mb-2" : "mb-4"}`}>
      <h3 className={`${small ? "text-sm" : "text-lg"} font-medium text-slate-900`}>{title}</h3>
      <button data-testid={testId} onClick={onAdd} className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1"><Plus className="h-3 w-3" />Add</button>
    </div>
  );
}

function DataTable({ headers, children, empty, emptyText }) {
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden">
      <Table>
        <TableHeader><TableRow className="bg-slate-50">{headers.map((h) => <TableHead key={h} className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{empty ? <TableRow><TableCell colSpan={headers.length} className="text-center py-8 text-sm text-slate-500">{emptyText}</TableCell></TableRow> : children}</TableBody>
      </Table>
    </div>
  );
}

function DelBtn({ onClick, testId }) {
  return <button data-testid={testId} onClick={onClick} className="p-1.5 hover:bg-red-50 rounded-sm transition-colors text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>;
}
